import { createRootRoute, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useAdminSession } from '@/lib/api/adminSession';
import { decideAdminGuard } from '@/lib/auth/adminGuard';
import { GuardErrorScreen } from '@/lib/auth/GuardErrorScreen';
import { GuardOfflineScreen } from '@/lib/auth/GuardOfflineScreen';
import { ADMIN_NAV, isPublicAdminPath } from '@/lib/nav/adminNav';
import { ThemeToggle } from '@/lib/theme/ThemeToggle';
import { AppShell, SidebarAction } from '@/ui/Shell';
import { ScreenLoading } from '@/ui/Loader';
import { navIconFor } from '@/ui/navIcons';
import { LogOutIcon } from '@/icons/nav';
import { PublicFrame } from '@/ui/PublicFrame';

export const Route = createRootRoute({
  component: AdminRootLayout,
});

// The AAL2/admin-session guard below is component-level (a plain render
// branch reading `useAdminSession()`, a TanStack Query hook), not a router
// `beforeLoad`. A `beforeLoad` guard would need the query client reachable
// from router context and would duplicate this same decision outside
// React's render cycle for no correctness gain here (fix round 1, item 10 —
// full rationale and the resulting "no loaders on admin routes" rule in
// portal/notes/T10.md). Every admin content route MUST stay leaf-only: no
// route in src/routes/admin/ may add its own `loader`/`beforeLoad` that
// assumes the guard already ran — a route only ever mounts via <Outlet/>
// inside the `allow` branch below, so the guard has always already run by
// the time any admin route component's body executes, but a loader
// attached to the route itself would run independently of that.
function AdminRootLayout() {
  const { session, isLoading: sessionLoading, signOut } = useAuth();
  const location = useLocation();
  const isPublic = isPublicAdminPath(location.pathname);

  const adminSessionQuery = useAdminSession();

  if (isPublic) {
    // Signed-in shouldn't sit on the sign-in form — hand off to the
    // protected branch below, which is the single place that decides
    // where a signed-in visitor actually belongs (not-available / step-up
    // / allow). Deliberately NOT gated on aal2 here: a signed-in non-admin
    // at aal1 must still leave /sign-in (to see "Not available"), and this
    // is also why SignInForm's own onSignedIn callbacks don't call
    // navigate() themselves — a component-level navigate racing the
    // AuthProvider's deferred (setTimeout 0, see AuthProvider.tsx)
    // session-context update landed on /sign-in with session still null
    // for one render, which this root guard's OWN "no session" branch then
    // bounced straight back to /sign-in — a real bug caught by hand
    // against the local stack, not hypothetical.
    if (session && location.pathname === '/sign-in') {
      return <Navigate to="/" />;
    }
    return (
      <PublicFrame tone="admin">
        <Outlet />
      </PublicFrame>
    );
  }

  const decision = decideAdminGuard({
    hasSession: !!session,
    sessionLoading,
    adminSessionLoading: adminSessionQuery.isLoading,
    adminSessionError: adminSessionQuery.isError,
    adminSessionFetchStatus: adminSessionQuery.fetchStatus,
    adminSession: adminSessionQuery.data,
  });

  if (decision === 'loading') {
    return <ScreenLoading />;
  }
  if (decision === 'sign-in') {
    return <Navigate to="/sign-in" />;
  }
  if (decision === 'offline') {
    return <GuardOfflineScreen onSignOut={signOut} />;
  }
  if (decision === 'error') {
    // fix round 1, item 4: a failed admin-session call used to leave this
    // screen on "Loading…" forever — decideAdminGuard now surfaces it.
    return <GuardErrorScreen onRetry={() => void adminSessionQuery.refetch()} onSignOut={signOut} />;
  }
  if (decision === 'step-up') {
    return <Navigate to="/sign-in/mfa" search={{ next: location.pathname }} />;
  }
  if (decision === 'not-available') {
    // Deliberately a flat, uninformative shell (task-10 brief: "403 → flat
    // 'Not available'") — a signed-in non-admin gets no hint about what
    // exists behind this gate, no nav, no admin-shaped chrome.
    return (
      <div className="flex min-h-full items-center justify-center" data-theme-scope="admin">
        <div className="text-center">
          <p className="text-h2 font-extrabold text-ink">Not available</p>
          <button type="button" onClick={() => void signOut()} className="mt-sm text-body text-ink-2 underline">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // decision === 'allow'
  return (
    <div className="min-h-full" data-theme-scope="admin">
      <AppShell
        tone="admin"
        productName="Admin"
        nav={ADMIN_NAV.map((item) => ({ ...item, icon: navIconFor(item.to) }))}
        sidebarBottom={
          <div className="flex flex-col gap-[2px]">
            <ThemeToggle tone="ink-bar" />
            <SidebarAction tone="admin" icon={<LogOutIcon />} onClick={() => void signOut()}>
              Sign out
            </SidebarAction>
          </div>
        }
      >
        <Outlet />
      </AppShell>
    </div>
  );
}
